import { getPublicRuntimeConfig } from '@helpers/getPublicRuntimeConfig';
import { ACCEPT_CLIENT_SIDE_ERROR_LOGS, isBrowser } from '@packages/common';

type ConsoleMethods = keyof Console;

// Some users send us errors repetatively thousands of times
let allowedErrorCount = 100;
const saveToDatadog = (method: string, data: any) => {
  if (allowedErrorCount <= 0) {
    return;
  }
  allowedErrorCount--;
  fetch(ACCEPT_CLIENT_SIDE_ERROR_LOGS, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: `json=${JSON.stringify({ method, ...data })}`,
  }).catch(() => {});
};

const initLogger = () => {
  const originalConsole = {} as Console;

  const extendConsoleLogger = (console: Console, methods: ConsoleMethods[]) => {
    methods.forEach((method) => {
      originalConsole[method] = console[method];
      console[method] = function (...args: any) {
        originalConsole[method].apply(console, args);
        saveToDatadog(method, {
          errorMsg: args[0],
          ...args,
          stack: new Error().stack,
          // Force-extract non-enumerable (and not serializable) fields
          errors: args
            .filter((arg: any) => arg instanceof Error)
            .map((arg: Error) => ({
              ...arg,
              message: arg.message,
              stack: arg.stack,
            })),
        });
      };
    });
  };

  extendConsoleLogger(window.console, ['error', 'warn']);

  window.onerror = (errorMsg, url, lineNumber, column, errorObj) => {
    const error = errorObj instanceof Error ? errorObj : new Error(JSON.stringify(errorObj));
    // Force-extract non-enumerable (and not serializable) fields
    const { message, stack } = error;
    saveToDatadog('window.onerror', {
      ...error,
      errorMsg,
      message,
      url,
      lineNumber,
      column,
      stack,
    });
  };

  window.addEventListener('unhandledrejection', function (event) {
    const error = event.reason instanceof Error ? event.reason : new Error(JSON.stringify(event.reason));
    // Force-extract non-enumerable (and not serializable) fields
    const { message, stack } = error;
    saveToDatadog('unhandledrejection', {
      ...error,
      errorMsg: message,
      stack,
    });
    event.preventDefault();
  });
};

const TARGET_ENV = getPublicRuntimeConfig('TARGET_ENV');

interface ClientSideLoggerProps {
  isEnabled?: boolean;
}

export function ClientSideLogger({ isEnabled = TARGET_ENV === 'production' }: ClientSideLoggerProps): JSX.Element {
  if (isBrowser() && isEnabled) {
    initLogger();
  }

  return null;
}
